import Chip from '@material-ui/core/Chip'
import Dialog from '@material-ui/core/Dialog'
import DialogActions from '@material-ui/core/DialogActions'
import DialogContent from '@material-ui/core/DialogContent'
import DialogTitle from '@material-ui/core/DialogTitle'
import Grid from '@material-ui/core/Grid'
import Link from '@material-ui/core/Link'
import { makeStyles, withStyles } from '@material-ui/core/styles'
import Tooltip from '@material-ui/core/Tooltip'
import Typography from '@material-ui/core/Typography'
import IconContentAdd from '@material-ui/icons/AddCircleOutline'
import IconCancel from '@material-ui/icons/Cancel'
import CloseIcon from '@material-ui/icons/Close'
import DoneIcon from '@material-ui/icons/Done'
import EditIcon from '@material-ui/icons/Edit'
import { KeyboardDateTimePicker } from '@material-ui/pickers'
import { get, isEmpty } from 'lodash'
import { DateTime } from 'luxon'
import { createElement, useState } from 'react'
import {
  AutocompleteInput,
  BooleanField as ReactAdminBooleanField,
  Button,
  Datagrid,
  Error,
  FormWithRedirect,
  FunctionField,
  Labeled,
  Loading,
  NumberField,
  NumberInput,
  ReferenceInput,
  ReferenceManyField,
  required,
  SaveButton,
  TextField,
  TextFieldProps,
  TextInput,
  useInput,
  useNotify,
  useQueryWithStore,
  useRefresh,
} from 'react-admin'
import { Field, useForm, useFormState } from 'react-final-form'
import { Document, Page } from 'react-pdf'
import { formatCurrency } from 'shared/helpers/formatters'

import { captureExceptionIfProduction, getFormattedDate, getLoadStop, safeMatch } from 'helpers/utils'
import {
  DESTINATION,
  DriverStatus,
  EquipmentStatus,
  JobStatus,
  JobStatusDetail,
  LABEL_DROPOFF_DATE,
  LABEL_ORIGIN_TO_DESTINATION,
  LABEL_PICKUP_DATE,
  LOAD_STOP_WINDOW_TYPES,
  RELATED_PROPERTY_FIELD_TYPES,
  STATUS_ICON_STYLES,
  STATUS_ICONS,
} from '../../helpers/constants'
import ListPagination from '../Pagination'
import { UserLink } from '../User/UserLink'

// same as react-admin's BooleanField, but with a green checkmark if true and red cross mark if false
export const CustomBooleanField = (props: any) => (
  <ReactAdminBooleanField
    {...props}
    TrueIcon={() => <DoneIcon htmlColor="#00AA6D" />}
    FalseIcon={() => <CloseIcon htmlColor="#EC001B" />}
  />
)

CustomBooleanField.defaultProps = {
  addLabel: true,
}

// Useful in forms for conditionally displaying fields
export const ConditionalInput = ({ when, is, oneOf = undefined, isNotEmpty = false, children }: any) => (
  <Field name={when} subscription={{ value: true }}>
    {({ input: { value } }) => {
      if (is !== undefined) {
        return value === is ? children : null
      } else if (oneOf) {
        return oneOf.includes(value) ? children : null
      } else if (isNotEmpty) {
        return value ? children : null
      }
      return null
    }}
  </Field>
)

export const GenericFormField = ({ label }: any) => (
  <Typography component="span" variant="body2">
    {label}
  </Typography>
)

export const LocalizedStopDateTimeField = (props: any) => {
  if (!props.record) {
    return null
  }
  const dateString = get(props.record, props.source)
  const localizedDateString = getFormattedDate(dateString, props.record.location_timezone)
  return (
    <Typography component="span" variant="body2">
      {localizedDateString}
    </Typography>
  )
}

export const CustomDateField = ({ record = null, source, format = 'LLL d, yyyy' }: any) => {
  if (!record || !source) {
    return null
  }
  const dateString = get(record, source)
  if (!dateString) {
    return null
  }
  const formattedDateString = DateTime.fromISO(dateString).toFormat(format)
  return (
    <Typography component="span" variant="body2">
      {formattedDateString}
    </Typography>
  )
}

export const CustomLinkifiedField = ({ record, source, label }: any) => {
  if (!record || !source) {
    return null
  }
  const text = get(record, source)
  const linkifiedText = (text: any) => {
    if (!text) return null
    const urlRegex = /(https?:\/\/[^\s]+)/g
    return text.split(urlRegex).map((part: any) => {
      if (part.match(urlRegex)) {
        return <Link href={part}>{part}</Link>
      }
      return part
    })
  }
  return <LabeledTypographyField variant="body2" label={label} value={linkifiedText(text)} />
}

export const DateTimeInput24hr = (props: any) => {
  const {
    input: { onChange, value },
  } = useInput(props)

  const { source, customOnChange } = props
  let dateValue = value !== '' ? value : null
  let finalOnChange = customOnChange
    ? (value: any) => {
        onChange(value)
        customOnChange(value)
      }
    : onChange

  // Note that the required={true} tag prevents form submission with an empty field.
  // The error prop highlights the field in red when it is empty, but does not prevent
  // form submission.
  return (
    <KeyboardDateTimePicker
      value={dateValue}
      name={source}
      ampm={false}
      onChange={finalOnChange}
      format="MM/dd/yyyy HH:mm"
      required={true}
      error={dateValue ? false : true}
      helperText={dateValue ? '' : 'Required'}
    />
  )
}

// This is used in LoadCreate & ApproveJob (setting the end window equal to start window on change).
// This component should be used within an ArrayInput of a form.
export const StopDateTimeInputWindow = ({ source, ...props }: any) => {
  const form = useForm()
  const startWindowSource = `${source}.date_start`
  const endWindowSource = `${source}.date_end`
  return (
    <div>
      <Labeled label="Date start">
        <DateTimeInput24hr
          source={startWindowSource}
          customOnChange={(value: any) => form.change(endWindowSource, value)}
          {...props}
        />
      </Labeled>
      <br />
      <Labeled label="Date end">
        <DateTimeInput24hr source={endWindowSource} {...props} />
      </Labeled>
    </div>
  )
}

export const StopSequenceNumberWrapper = ({ source, ...props }: any) => {
  const sequenceSource = `${source}.sequence_number`
  return <StopSequenceNumberField source={sequenceSource} {...props} />
}

export const StopSequenceNumberField = (props: any) => {
  const {
    input: { onChange },
  } = useInput(props)
  return <NumberInput source={props.source} {...props} onChange={onChange} />
}

export const OriginDestinationField = ({ record }: any) => {
  return (
    <span>
      {record.origin_city}, {record.origin_state} &rarr; {record.dest_city}, {record.dest_state}
    </span>
  )
}

export const CustomLoadOriginDestinationField = ({ record }: any) => {
  return (
    <span>
      {record.origin_location} &rarr; {record.dest_location}
    </span>
  )
}

export const PDFPreviewField = (props: any) => {
  const [numPages, setNumPages] = useState(null)
  const [pageNumber, setPageNumber] = useState(1)

  function onDocumentLoadSuccess({ numPages }: any) {
    setNumPages(numPages)
    setPageNumber(1)
  }

  function changePage(offset: any) {
    setPageNumber((prevPageNumber) => prevPageNumber + offset)
  }

  function previousPage() {
    changePage(-1)
  }

  function nextPage() {
    changePage(1)
  }

  return (
    <>
      <Document file={props.record.rawFile} onLoadSuccess={onDocumentLoadSuccess}>
        <Page pageNumber={pageNumber} />
      </Document>
      <div>
        <p>
          Page {pageNumber || (numPages ? 1 : '--')} of {numPages || '--'}
        </p>
        <button type="button" disabled={pageNumber <= 1} onClick={previousPage}>
          Previous
        </button>
        <button type="button" disabled={pageNumber >= (numPages ? numPages : -1)} onClick={nextPage}>
          Next
        </button>
      </div>
    </>
  )
}

export const RelatedPropertyField = ({ propertyName, relatedResource, source, record = {}, type }: any) => {
  const propertyId = get(record, propertyName)
  const { loaded, error, data } = useQueryWithStore({
    type: 'getOne',
    resource: relatedResource,
    payload: { id: propertyId },
  })
  if (!loaded) {
    return <Loading />
  }
  if (error) {
    return <Error error={error} />
  }
  switch (type) {
    case RELATED_PROPERTY_FIELD_TYPES.text:
      return (
        <Typography component="span" variant="body2">
          {get(data, source)}
        </Typography>
      )
    case RELATED_PROPERTY_FIELD_TYPES.stopDateTime:
      let destStop = getLoadStop(get(data, source), DESTINATION)
      if (!destStop) {
        return null
      }
      return <LocalizedStopDateTimeField record={destStop} source={LOAD_STOP_WINDOW_TYPES.start} />
    default:
      return null
  }
}

RelatedPropertyField.defaultProps = {
  addLabel: true,
}

export const OptionalRelatedPropertyField = ({
  propertyName,
  relatedResource,
  source,
  record = {},
  type,
}: any) => {
  return get(record, propertyName) !== null ? (
    <RelatedPropertyField
      propertyName={propertyName}
      relatedResource={relatedResource}
      source={source}
      record={record}
      type={type}
    />
  ) : null
}

OptionalRelatedPropertyField.defaultProps = {
  addLabel: true,
}

export const FullNameField = ({ record = {}, className }: any) => (
  <span className={className}>
    {record.first_name} {record.last_name}
  </span>
)

export const EmailField = ({ record = {}, className }: any) => (
  <span className={className}>{record.email}</span>
)

export const FullNameOrIdField = ({ record = {} }: any) => (
  <span>
    {record.first_name || record.last_name ? `${record.first_name} ${record.last_name}` : record.id}
  </span>
)

export const LabeledTypographyField = ({ label, variant, value, typographyProps = {} }: any = {}) => (
  <Labeled label={label}>
    <Typography component="span" variant={variant} {...typographyProps}>
      {value}
    </Typography>
  </Labeled>
)

export const LabeledUrlField = ({ label, url }: any) => (
  <Typography>
    <Link href={url} variant="body1">
      {label}
    </Link>
  </Typography>
)

export const StatusIconField = withStyles(STATUS_ICON_STYLES)(({ classes, ...props }: any) => {
  const status = STATUS_ICONS[props.record[props.source]]
  if (!status) {
    return null
  }
  return (
    <Tooltip title={status.name} className={classes[status.name]}>
      {createElement(status.icon)}
    </Tooltip>
  )
})

type StatusTextFieldClassesProp = TextFieldProps['classes'] & keyof typeof STATUS_ICON_STYLES

export const StatusTextField = withStyles<StatusTextFieldClassesProp>(STATUS_ICON_STYLES)(
  ({ classes, ...props }: any) => {
    const status_name = props.record[props.source]
    return <TextField className={classes[status_name]} {...props} />
  }
)

StatusTextField.defaultProps = TextField.defaultProps

export const UnderConstructionField = () => {
  return (
    <Typography component="span" variant="body2">
      UNDER CONSTRUCTION
    </Typography>
  )
}

export const HoursDetailsField = ({ record = {} }: any) => {
  return (
    <Typography component="span" variant="body2">
      {record.drive_hours} driving, {record.dwell_hours} dwell, {record.on_duty_hours} on duty,{' '}
      {record.off_duty_hours} off duty
    </Typography>
  )
}
HoursDetailsField.defaultProps = {
  addLabel: true,
  label: 'Hours Details',
}

export const RevenueDetailsField = ({ record = {} }: any) => {
  const revenue_per_loaded_mile = record.revenue_per_loaded_mile
    ? `, $${record.revenue_per_loaded_mile} / loaded mi`
    : ''
  const revenue_label =
    record.revenue > 0.0
      ? `$${record.revenue}, $${record.revenue_per_hour} / hr, $${record.revenue_per_mile} / mi${revenue_per_loaded_mile}`
      : ''
  return (
    <Typography component="span" variant="body2">
      {revenue_label}
    </Typography>
  )
}
RevenueDetailsField.defaultProps = {
  addLabel: true,
  label: 'Revenue Details',
}

export const DateWindowField = ({ record = {} }: any) => {
  const startDate = getFormattedDate(record.date_start, record.location_timezone)
  const endDate = getFormattedDate(record.date_end, record.location_timezone)

  return (
    <Typography component="span" variant="body2">
      {startDate} to {endDate}
    </Typography>
  )
}
DateWindowField.defaultProps = {
  addLabel: true,
  label: 'Date Window',
}

export const JobDisplayGroup = (props: any) => {
  const useStyles = makeStyles({
    table: {
      tableLayout: 'fixed',
    },
    column: {
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    },
  })

  const classes = useStyles()

  return (
    <Datagrid
      {...props}
      classes={{ table: classes.table, rowCell: classes.column }}
      rowClick={(id: string) => {
        window.open(`/jobs/${id}/show`, '_blank')
      }}
    >
      <TextField source="human_id" label="Job ID" />
      <TextField source="load.broker.name" label="Broker" />
      <TextField source="load.source" label="Load source" />
      <TextField source="load.source_human_id" label="Load source ID" />
      {props.showUser && (
        <FunctionField
          onClick={(e) => e.stopPropagation() /* Prevents row click action */}
          label="Assigned driver"
          render={(record: any) => <UserLink user={get(record, 'assigned_to')} />}
        />
      )}
      <FunctionField
        label={LABEL_ORIGIN_TO_DESTINATION}
        render={(record: any) => <OriginDestinationField record={record.load} />}
      />
      <FunctionField
        label={LABEL_PICKUP_DATE}
        sortBy="load__origin_pickup_date"
        render={(record: any) => (
          <LocalizedStopDateTimeField record={record.load} source="origin_pickup_date" />
        )}
      />
      <FunctionField
        label={LABEL_DROPOFF_DATE}
        sortBy="load__dest_delivery_date"
        render={(record: any) => (
          <LocalizedStopDateTimeField record={record.load} source="dest_delivery_date" />
        )}
      />
      <TextField source="status_detail" />
      <NumberField source="gross_earnings" />
    </Datagrid>
  )
}

export const JSONField = (props: any) => {
  if (!props.record) {
    return null
  }
  const data = get(props.record, props.source)
  return (
    <div>
      <pre>{JSON.stringify(data, null, 2)}</pre>
    </div>
  )
}

export const SimpleChipField = ({ record }: any) => {
  return record ? <Chip label={record} clickable={false} /> : null
}

SimpleChipField.defaultProps = {
  addLabel: true,
}

export const JobsReferenceManyList = ({ target, showUser = false, is_premium, ...props }: any) => {
  const past_job_filter = is_premium
    ? { status_detail: [JobStatusDetail.PaymentIssued] }
    : { status: [JobStatus.Past] }
  return (
    <div>
      <Labeled label="Pending">
        <ReferenceManyField
          reference="jobs"
          target={target}
          filter={{ status: JobStatus.Pending }}
          sort={{ field: 'load__origin_pickup_date', order: 'DESC' }}
          {...props}
        >
          <JobDisplayGroup showUser={showUser} />
        </ReferenceManyField>
      </Labeled>
      <br />
      <Labeled label="Future">
        <ReferenceManyField
          reference="jobs"
          target={target}
          label="Future"
          filter={{ status: JobStatus.Future }}
          sort={{ field: 'load__origin_pickup_date', order: 'DESC' }}
          {...props}
        >
          <JobDisplayGroup showUser={showUser} />
        </ReferenceManyField>
      </Labeled>
      <br />
      <Labeled label="Current">
        <ReferenceManyField
          reference="jobs"
          target={target}
          label="Current"
          filter={{ status: JobStatus.Current }}
          sort={{ field: 'load__origin_pickup_date', order: 'DESC' }}
          {...props}
        >
          <JobDisplayGroup showUser={showUser} />
        </ReferenceManyField>
      </Labeled>
      <br />
      <Labeled label="Past">
        <ReferenceManyField
          reference="jobs"
          target={target}
          label="Past"
          perPage={100}
          filter={past_job_filter}
          sort={{ field: 'load__origin_pickup_date', order: 'DESC' }}
          pagination={<ListPagination />}
          {...props}
        >
          <JobDisplayGroup showUser={showUser} />
        </ReferenceManyField>
      </Labeled>
    </div>
  )
}

const blackGreyStyles = makeStyles({
  black: { color: '"#000000' },
  grey: { color: '#e0e0e0' },
})

const conditionalGreyed =
  (WrappedComponent: typeof TextField): typeof TextField =>
  (props: any) => {
    const classes = blackGreyStyles()
    const { condition, record } = props
    return <WrappedComponent className={condition(record) ? classes['grey'] : classes['black']} {...props} />
  }

/**
 * TextField component that greys out text if condition callback evaluates to true on record.
 */
export const ConditionalGreyedTextField = conditionalGreyed(TextField)
// Ensure the original component defaultProps are still applied as they may be used by its parents (such as the `Show` component):
ConditionalGreyedTextField.defaultProps = TextField.defaultProps

export const CurrencyField = ({ record = null, source, toFixed = 0, removeDecimals = true }: any) => {
  if (!record || !source) {
    return null
  }
  const value = get(record, source)

  if (!value && value !== 0) {
    return null
  }

  const formattedValue = toFixed ? value.toFixed(toFixed) : value

  return (
    <Typography component="span" variant="body2">
      {formatCurrency(formattedValue, { removeDecimals: removeDecimals && toFixed === 0 })}
    </Typography>
  )
}

export const ShowIfContains = ({ children, field_name, contain_values }: any) => {
  const { values } = useFormState()
  if (contain_values.includes(values[field_name])) {
    return children
  }
  return null
}

export const SimpleCalloutField = ({ styleProps, label, labelVariant }: any) => (
  <div style={{ display: 'flex' }}>
    <Grid container alignItems="center" direction="row" spacing={2}>
      <Grid item {...styleProps}>
        <Typography variant={labelVariant || 'body1'} component="span">
          {label}
        </Typography>
      </Grid>
    </Grid>
  </div>
)

export const CalloutField = ({ icon: CalloutIcon, iconProps, label, labelVariant }: any) => (
  <div style={{ display: 'flex' }}>
    <Grid container alignItems="center" direction="row" spacing={2}>
      <Grid item>
        <CalloutIcon fontSize="large" {...iconProps} />
      </Grid>
      <Grid item>
        <Typography variant={labelVariant || 'body1'} component="span">
          {label}
        </Typography>
      </Grid>
    </Grid>
  </div>
)

const noScrollStyles = makeStyles({
  /* Chrome, Safari, Edge, Opera */
  numberInput: {
    '& input::-webkit-outer-spin-button': {
      '-webkit-appearance': 'none',
      margin: 0,
    },
    '& input::-webkit-inner-spin-button': {
      '-webkit-appearance': 'none',
      margin: 0,
    },

    /* Firefox */
    '& input[type=number]': {
      '-moz-appearance': 'textfield',
    },
  },
})
export const SimpleNumberInput = (props: any) => {
  const classes = noScrollStyles()
  return (
    <NumberInput
      {...props}
      className={classes.numberInput}
      inputProps={{ onWheel: (event) => event.currentTarget.blur() }}
    />
  )
}

export const AddOrEditTextField = ({ initialPayload, label, source, api, id, initialValue }: any) => {
  const [showDialog, setShowDialog] = useState(false)
  const [loading, setLoading] = useState(false)

  const notify = useNotify()
  const refresh = useRefresh()
  const handleClick = () => setShowDialog(true)
  const handleClose = () => setShowDialog(false)

  const handleSubmit = async (values: any) => {
    setLoading(true)
    api(id, values)
      .then(() => {
        setShowDialog(false)
        setLoading(false)
        notify('Input saved!', { type: 'info' })
        refresh()
      })
      .catch((error: any) => {
        setLoading(false)
        setShowDialog(false)
        notify(error.message, { type: 'error' })
        captureExceptionIfProduction(error)
      })
  }

  return (
    <>
      {isEmpty(initialValue) ? (
        <Button onClick={handleClick} label="ra.action.add">
          <IconContentAdd />
        </Button>
      ) : (
        <div style={{ display: 'flex' }}>
          <TextField source={source} />
          <Button onClick={handleClick}>
            <EditIcon />
          </Button>
        </div>
      )}
      <Dialog fullWidth open={showDialog} onClose={handleClose}>
        <DialogTitle>{label}</DialogTitle>
        <FormWithRedirect
          initialValues={initialPayload}
          save={handleSubmit}
          render={({ handleSubmitWithRedirect, saving }) => (
            <>
              <DialogContent>
                <TextInput source={source} initialValue={initialValue} />
              </DialogContent>
              <DialogActions>
                <Button label="ra.action.cancel" onClick={handleClose} disabled={loading}>
                  <IconCancel />
                </Button>
                <SaveButton
                  handleSubmitWithRedirect={handleSubmitWithRedirect}
                  saving={saving}
                  disabled={loading}
                />
              </DialogActions>
            </>
          )}
        />
      </Dialog>
    </>
  )
}

export const AssignedDriverReferenceInput = ({
  contactedLoadTeamId = null,
  disabled = false,
}: {
  contactedLoadTeamId?: string | null
  disabled?: boolean
}) => {
  const validateRequired = [required()]
  const matchSuggestion = (filter: any, choice: any) => {
    const normalizedFilter = filter.trim().toLowerCase()
    const normalizedFirstName = choice?.first_name?.trim().toLowerCase()
    const normalizedLastName = choice?.last_name?.trim().toLowerCase()
    const normalizedTeamName = choice?.team?.name?.trim().toLowerCase() || ''
    const normalizedEmail = choice?.email?.trim().toLowerCase()
    return (
      safeMatch(normalizedFirstName, normalizedFilter) ||
      safeMatch(normalizedLastName, normalizedFilter) ||
      safeMatch(`${normalizedFirstName} ${normalizedLastName}`, normalizedFilter) ||
      safeMatch(normalizedEmail, normalizedFilter) ||
      safeMatch(normalizedTeamName, normalizedFilter)
    )
  }
  let assignedDriverFilter = {
    is_team_driver: true,
    team__is_real: true,
    team__is_approved: true,
    driver_status: DriverStatus.Active,
    is_active: true,
    team: contactedLoadTeamId,
  }

  const inputText = (choice: any) => {
    if (!choice) {
      return null
    }
    return `${choice.first_name} ${choice.last_name}`
  }

  const OptionText = (choice: any) => {
    if (!choice) {
      return null
    }
    const user = choice.record
    if (!user) {
      return null
    }
    return (
      <div>
        <Typography component="span" variant="body1">
          {user.first_name} {user.last_name}
        </Typography>
        <br />
        <Typography component="span" variant="body2">
          {user.team?.name}
        </Typography>
      </div>
    )
  }

  return (
    <ReferenceInput
      source="assigned_to"
      label="Assigned driver"
      reference="users"
      filter={assignedDriverFilter}
      filterToQuery={(searchText) => ({ search: searchText })}
      validate={validateRequired}
      fullWidth={true}
      disabled={disabled}
    >
      <AutocompleteInput
        optionText={<OptionText />}
        inputText={inputText}
        matchSuggestion={matchSuggestion}
        fullWidth={true}
      />
    </ReferenceInput>
  )
}

export const AssignedTruckReferenceInput = ({
  contactedLoadTeamId = null,
  disabled = false,
}: {
  contactedLoadTeamId?: string | null
  disabled?: boolean
}) => {
  const validateRequired = [required()]
  const matchSuggestion = (filter: any, choice: any) => {
    const normalizedFilter = filter.trim().toLowerCase()
    const normalizedVehicleMake = choice?.vehicle_make?.trim().toLowerCase()
    const normalizedVehicleModel = choice?.vehicle_model?.trim().toLowerCase()
    const normalizedVehicleVin = choice?.vin_number?.trim().toLowerCase()
    return (
      safeMatch(normalizedVehicleMake, normalizedFilter) ||
      safeMatch(normalizedVehicleModel, normalizedFilter) ||
      safeMatch(`${normalizedVehicleMake} ${normalizedVehicleModel}`, normalizedFilter) ||
      safeMatch(normalizedVehicleVin, normalizedFilter)
    )
  }
  let assignedTruckFilter = {
    status: EquipmentStatus.Active,
    team: contactedLoadTeamId,
  }

  const inputText = (choice: any) => {
    if (!choice) {
      return null
    }
    return `${choice.vehicle_year} ${choice.vehicle_make} ${choice.vehicle_model}`
  }

  const OptionText = (choice: any) => {
    if (!choice) {
      return null
    }
    const truck = choice.record
    if (!truck) {
      return null
    }
    return (
      <div>
        <Typography component="span" variant="body1">
          {truck.vehicle_year} {truck.vehicle_make} {truck.vehicle_model}
        </Typography>
        <br />
        <Typography component="span" variant="body2">
          {truck.vin_number}
        </Typography>
      </div>
    )
  }

  return (
    <ReferenceInput
      source="assigned_truck"
      label="Assigned truck"
      reference="trucks"
      filter={assignedTruckFilter}
      filterToQuery={(searchText) => ({ search: searchText })}
      validate={validateRequired}
      fullWidth={true}
      disabled={disabled}
    >
      <AutocompleteInput
        optionText={<OptionText />}
        inputText={inputText}
        matchSuggestion={matchSuggestion}
        fullWidth={true}
      />
    </ReferenceInput>
  )
}
